import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SalaryScaleFormValues, SalaryScaleResponse } from '../../types';

import { convertPayloadForSave } from './utils/convertPayloadForSave';
import { schema } from './utils/schema';
import { convertFlagsArrayToObjects } from 'utils/misc';

import { SALARY_SCALES } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import ValidityFormElement from 'components/shared/CRUD/components/ValidityFormElement';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import BaseSection from './components/sections/BaseSection';
import ExternalSection from './components/sections/ExternalSection';
import FlagsSection from './components/sections/FlagsSection';

const rowDataConverter = (
  response: SalaryScaleResponse
): SalaryScaleFormValues => {
  return {
    ...response,
    valid: {
      from: response.valid.from ? new Date(response.valid.from) : null,
      to: response.valid.to ? new Date(response.valid.to) : null
    },
    flags: convertFlagsArrayToObjects(response.flags)
  };
};

export default function CreateEditSalaryScalesForm({
  handleClose,
  rowData
}: FormComponentProps<SalaryScaleResponse>) {
  const { t } = useTranslation('common');
  const formData = rowData ? rowDataConverter(rowData) : null;
  const { mutateAsync: save, isLoading } = useSaveResource({
    url: SALARY_SCALES,
    id: rowData?.salaryId,
    invalidateQueryKey: ['salaryScales', 'list']
  });

  const methods = useForm<SalaryScaleFormValues>({
    defaultValues: schema.cast(formData ?? undefined),
    resolver: yupResolver(schema)
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: SalaryScaleFormValues) => {
    const payload = convertPayloadForSave(data);
    save(payload).then(handleClose);
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('salaryScales.formSectionLabels.basis')}
        validateFields={['claId', 'scale', 'seniority', 'periodSalary']}
      >
        <BaseSection />
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('salaryScales.formSectionLabels.flags')}>
        <FlagsSection />
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('salaryScales.formSectionLabels.external')}
      >
        <ExternalSection />
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('salaryScales.formSectionLabels.validity')}
        validateFields={['valid.from', 'valid.to']}
      >
        <ValidityFormElement />
      </MultiStepForm.Section>

      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
}
